import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { UnlockedAchievement } from '@/features/achievements/Celebration';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface Dashboard {
  mode: 'quitting' | 'maintaining' | 'smoking';
  quitDate: string | null;
  isReductionPhase: boolean;
  secondsUntilQuitDate: number;
  streak: { currentSeconds: number; bestSeconds: number; cleanDays: number };
  reduction: { today: number; yesterday: number; baseline: number; target: number | null } | null;
  avoided: { total: number; today: number };
  savings: { totalCents: number; dailyCents: number; currency: string };
  health: {
    unlockedCount: number;
    totalCount: number;
    progressPct: number;
    next: { slug: string; titleEs: string; icon: string; reachedAt: string; progressPct: number } | null;
  };
  cravings: { resistedTotal: number; succeeded: number; total: number; successRatePct: number };
  treatment: {
    planId: number;
    brand: string;
    day: number;
    totalDays: number;
    progressPct: number;
    remainingDoses: number;
    adherencePct: number;
    endDate: string;
    nextDose: { id: number; scheduledAt: string } | null;
    /** La pauta terminó y quedó cerrada: la tarjeta pasa a la etapa siguiente. */
    finished: boolean;
    completedAt: string | null;
  } | null;
  /** La etapa posterior a la pauta (issue #55). */
  maintenance: {
    daysSince: number;
    phase: 'settling' | 'trial' | 'ready';
    daysToOffer: number;
    offerDue: boolean;
  } | null;
  /** La etapa posterior al tratamiento (#73). Para todo el que tenga Día D. */
  stage: {
    kind: 'cold_turkey' | 'post_pills';
    daysSinceQuit: number;
    daysSincePills: number | null;
    unsupported: 'fresh' | 'settling' | 'past' | null;
    beaten: number;
    mastered: number;
    situations: number;
  } | null;
  rpg: { level: number; xpInLevel: number; xpForNextLevel: number; progressPct: number; title: string };
  motivation: { text: string; kind: string };
  /** Achievements unlocked and not yet celebrated. */
  celebrate: UnlockedAchievement[];
}

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard(),
    queryFn: () => api<Dashboard>('/api/dashboard'),
    staleTime: 30_000,
  });
}

/**
 * Ticks once a second so the hero counter moves. Counting locally from the
 * server value avoids polling the API every second.
 */
export function useLiveSeconds(baseSeconds: number | undefined): number {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    setElapsed(0);
    if (baseSeconds === undefined) return;

    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [baseSeconds]);

  return (baseSeconds ?? 0) + elapsed;
}
